import { CategoryService } from './categoryService';
import { DossierService } from './dossierService';
import { PlatformService } from './platformService';
import { EditorialToolsService } from './editorialToolsService';

export interface ExtractedArticle {
  order: number;
  autor: string;
  thema: string;
  titel: string;
  untertitel: string;
  body: string;
  htmlBody: string;
  rawData: Record<string, unknown>;
}

interface ArticleExtractorApiArticle {
  order?: unknown;
  autor?: unknown;
  thema?: unknown;
  titel?: unknown;
  untertitel?: unknown;
  body?: unknown;
  html_body?: unknown;
}

interface ArticleExtractorApiResponse {
  articles?: unknown;
}

const PUBLISH_WEBHOOK_URL = 'https://n8n.koll.cc/webhook/0b3b4761-c0ea-47b8-b884-92bd7a1c4fe9';
const EF_ONLINE_PLATFORM_NAME = 'eigentümlich frei - Online';

interface EfOnlineMeta {
  kategorien: string;
  dossiers: string;
}

const asString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return String(value);
};

const asNumber = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeArticle = (item: ArticleExtractorApiArticle, index: number): ExtractedArticle => ({
  order: asNumber(item.order, index + 1),
  autor: asString(item.autor),
  thema: asString(item.thema),
  titel: asString(item.titel),
  untertitel: asString(item.untertitel),
  body: asString(item.body),
  htmlBody: asString(item.html_body),
  rawData: { ...(item as Record<string, unknown>) }
});

const normalizeResponse = (data: unknown): ExtractedArticle[] => {
  if (Array.isArray(data)) {
    return data
      .filter((item): item is ArticleExtractorApiArticle => typeof item === 'object' && item !== null)
      .map((item, index) => normalizeArticle(item, index));
  }

  if (data && typeof data === 'object') {
    const articles = (data as ArticleExtractorApiResponse).articles;
    if (!Array.isArray(articles)) {
      throw new Error('Die Antwort enthält kein gültiges "articles"-Array.');
    }

    return articles
      .filter((item): item is ArticleExtractorApiArticle => typeof item === 'object' && item !== null)
      .map((item, index) => normalizeArticle(item, index));
  }

  throw new Error('Unerwartetes Antwortformat vom Endpunkt.');
};

const parseHtmlResponse = (data: unknown): string => {
  if (typeof data === 'string') {
    if (!data.trim()) {
      throw new Error('Die Antwort enthält keinen HTML-Inhalt.');
    }
    return data;
  }

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    for (const key of ['html', 'content', 'body', 'result']) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }

  throw new Error('Unerwartetes Antwortformat vom HTML-Endpunkt.');
};

export class ArticleExtractorService {
  private static efOnlineMetaCache: EfOnlineMeta | null = null;

  private static normalizeName(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private static async loadEfOnlineMeta(): Promise<EfOnlineMeta> {
    if (this.efOnlineMetaCache) {
      return this.efOnlineMetaCache;
    }

    const platforms = await PlatformService.getAllPlatforms();
    const wantedName = this.normalizeName(EF_ONLINE_PLATFORM_NAME);
    const platform = platforms.find((item) => this.normalizeName(item.name) === wantedName);

    if (!platform) {
      throw new Error(`Plattform "${EF_ONLINE_PLATFORM_NAME}" wurde nicht gefunden.`);
    }

    const [categories, dossiers] = await Promise.all([
      CategoryService.getCategoriesByPlatform(platform.id),
      DossierService.getDossiersByPlatform(platform.id)
    ]);

    const meta: EfOnlineMeta = {
      kategorien: categories.map((category) => category.name.trim()).filter(Boolean).join(', '),
      dossiers: dossiers.map((dossier) => dossier.title.trim()).filter(Boolean).join(', ')
    };

    this.efOnlineMetaCache = meta;
    return meta;
  }

  static async extractFromIdml(file: File): Promise<ExtractedArticle[]> {
    const data = await EditorialToolsService.processIdml<unknown>(file, 'articles');
    const articles = normalizeResponse(data);

    if (articles.length === 0) {
      throw new Error('Keine Artikel in der Antwort gefunden.');
    }

    return articles;
  }

  static async convertIdmlToHtml(file: File): Promise<string> {
    return parseHtmlResponse(await EditorialToolsService.processIdml<string>(file, 'html'));
  }

  static async publishArticle(
    article: ExtractedArticle,
    information: string,
    schedule: { publishDate: string; publishTime: string }
  ): Promise<void> {
    const efOnlineMeta = await this.loadEfOnlineMeta();
    const rawHtmlBody = typeof article.rawData.html_body === 'string' ? article.rawData.html_body : '';
    const payload = {
      ...article.rawData,
      html_body: `${rawHtmlBody}${information}`,
      kategorien: efOnlineMeta.kategorien,
      dossiers: efOnlineMeta.dossiers,
      publishDate: schedule.publishDate,
      publishTime: schedule.publishTime
    };

    const response = await fetch(PUBLISH_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Webhook-Fehler (${response.status})`);
    }
  }
}
